import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getApiUrl } from '../utils/apiUtils';
import { debugError } from '../utils/debug';

/**
 * VersionDisplay Component
 * Shows backend version information
 * 
 * Loads version once on mount (on app start/browser refresh).
 * No auto-refresh - version updates when user refreshes page.
 * 
 * Usage:
 * <VersionDisplay /> - Minimal inline version
 * <VersionDisplay detailed /> - Full version info with commit hash
 */
const VersionDisplay = ({ detailed = false, className = '' }) => {
  const { user } = useUser();
  const [versionInfo, setVersionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const API_URL = getApiUrl(); // Get at runtime
        
        // Add timeout to prevent infinite loading
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch(`${API_URL}/version`, {
          method: 'GET',
          signal: controller.signal,
          // No auth needed for version endpoint
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch version`);
        }
        
        const data = await response.json();
        setVersionInfo(data);
        setError(null);
      } catch (err) {
        if (err.name === 'AbortError') {
          debugError('[VersionDisplay] Request timeout after 5 seconds');
          setError('Timeout: Backend not responding');
        } else {
          debugError('[VersionDisplay] Failed to fetch version:', err);
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVersion();
    
    // Load once on mount only - no auto-refresh
    // Version will update on page refresh/app restart
  }, []);

  if (loading) {
    return detailed ? (
      <div className={`text-xs text-gray-500 ${className}`}>Loading version...</div>
    ) : (
      <span className={`text-xs text-gray-500 ${className}`}>v...</span>
    );
  }

  if (error || !versionInfo) {
    if (detailed) {
      return (
        <div className={`text-xs space-y-1 ${className}`}>
          <div className="text-red-400">Version unavailable</div>
          {error && <div className="text-red-500 text-[10px]">{error}</div>}
        </div>
      );
    }
    // For minimal display, show "v?" or nothing
    return <span className={`text-xs text-gray-400 ${className}`} title={error || 'Version unavailable'}>v?</span>;
  }

  if (detailed) {
    return (
      <div className={`text-xs text-gray-500 space-y-1 ${className}`}>
        <div>Backend: v{versionInfo.version}</div>
        <div className="text-gray-600">Commit: {versionInfo.git?.commit_short || 'unknown'}</div>
        <div className="text-gray-600">
          Built: {versionInfo.build_timestamp ? new Date(versionInfo.build_timestamp).toLocaleString() : 'unknown'}
        </div>
      </div>
    );
  }

  // Minimal inline version
  return (
    <span className={`text-xs text-gray-500 ${className}`} title={`Backend v${versionInfo.version} (${versionInfo.git?.commit_short || 'unknown'})`}>
      v{versionInfo.version}
    </span>
  );
};

export default VersionDisplay;

